// billing-plugin/client/components/BillingView.js

import { ComponentBase } from "../../core/ComponentBase.js";
import { RpcDataProvider } from "../../core/RpcDataProvider.js";
import {z} from 'zod';

export class BillingView extends ComponentBase {
    static styles = `
        .billing-view-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%; 
            overflow: hidden;
        }
        .billing-summary-row {
            flex-shrink: 0; 
        }
        .billing-history-container {
            flex-grow: 1; 
            min-height: 0; /* Flexbox fix for overflow */
            display: flex; /* Make it a flex container */
            flex-direction: column; /* Stack children vertically */
        }
        /* --- NEW: Ensure the DataView component itself fills the container --- */
        .billing-history-container > .data-view {
            flex-grow: 1; /* The DataView will now expand to fill the space */
        }
        /* --- END NEW --- */

        .billing-summary-card { text-align: center; }
        .billing-summary-card .value { font-size: 2.25rem; font-weight: 500; }
        .billing-summary-card .label { font-size: 0.9rem; color: var(--bs-secondary-color); }
    `;

    constructor(config) {
        super(config);
        this.billingInfo = null;
        this.isLoading = true;
        this.historyView = null;
        this._boundHandleBalanceUpdate = this.loadData.bind(this);
        this.applyStyles();
    }

    async initialize() {
        this.historyView = await this.createManagedComponent("DataView", null, {
            dataProvider: new RpcDataProvider({ 
                appClient: this.appClient, 
                rpcClass: "BillingRpc", 
                rpcMethod: "getChargeRecordsForCurrentUser" ,
                autoLoad: false,
            }),
            title: "Transaction History",
            viewMode: 'grid',
            autoLoad: false,
            fullHeight: true, // Tell the DataView to manage its own height and internal scroll
            fields: [
                { name: 'createdAt', label: 'Date', render: (val) => new Date(val).toLocaleString() },
                { name: 'description', label: 'Description' },
                { name: 'type', label: 'Type', render: (val) => `<span class="badge bg-secondary text-capitalize">${val.replace('_', ' ')}</span>` },
                { name: 'amount', label: 'Amount', render: (val) => this.formatCurrency(val) }
            ]
        });

        this.appClient.events.local.on('userBalanceUpdated', this._boundHandleBalanceUpdate);
        await super.initialize();
    }

    async initAfterLogin() {
        await this.loadData();
    }

    async loadData() {
        this.isLoading = true;
        this.render();
        try {
            this.billingInfo = await this.appClient.rpcClient.call('BillingRpc.getBillingInfoForCurrentUser');
            this.historyView?.refreshData();
        } catch (error) {
            this.log.error("Failed to load billing info:", error);
            this.appClient.notifier.error("Could not load your billing information.");
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    async handleRechargeClick() {
        const form = await this.createManagedComponent("DataForm", null, {
            layout: 'dialog',
            title: 'Recharge Account Balance',
            schema: z.object({ amount: z.number().positive("Amount must be positive.") }),
            fields: {
                amount: { type: 'number', label: 'Amount to Add (in INR)', required: true }
            },
            actions: { save: true, cancel: true }
        });

        form.on('dataSaved', async (formData) => {
            try {
                const amountInCents = Math.round(formData.amount * 100);
                
                const paymentPayload = {
                    amount: amountInCents,
                    currency: this.billingInfo?.planSnapshot?.currency || 'INR',
                    description: 'Account Recharge',
                    metadata: {
                        userId: this.appClient.getSharedData('userId'),
                        subscriptionId: this.billingInfo.targetId
                    }
                };

                this.appClient.events.local.emit('initiatePayment', paymentPayload);
                this.appClient.notifier.info("Redirecting to payment gateway...");

            } catch (error) {
                this.appClient.notifier.error(`Could not initiate recharge: ${error.message}`);
            } finally {
                form.destroy();
            }
        });
        form.on('cancel', () => form.destroy());
        form.show();
    }

    render() {
        if (!this.container) this.container = document.createElement('div');
        this.container.className = 'billing-view-wrapper p-3';
        this.container.innerHTML = '';

        if (this.isLoading) {
            this.container.innerHTML = `<div class="text-center p-5"><div class="spinner-border" role="status"></div></div>`;
            return this.container;
        }

        const summaryRow = document.createElement('div');
        summaryRow.className = 'row g-4 billing-summary-row';
        summaryRow.innerHTML = `
            <div class="col-md-4">
                <div class="card billing-summary-card h-100">
                    <div class="card-body">
                        <div class="label text-uppercase">Current Plan</div>
                        <div class="value">${this.billingInfo?.planSnapshot?.name || 'N/A'}</div>
                        <div class="label text-uppercase" style="font-size: 0.7rem;">Subscription ID: <span style="font-weight: normal;">${this.billingInfo?.targetId || 'N/A'}</span></div>
                        <div class="label text-uppercase" style="font-size: 0.7rem;">Service Code: <span style="font-weight: normal;">${this.billingInfo?.serviceCode || 'N/A'}</span></div>
                    </div>
                </div>
            </div>
            <div class="col-md-4">
                <div class="card billing-summary-card h-100">
                    <div class="card-body">
                        <div class="label text-uppercase">Account Balance</div>
                        <div class="value">${this.formatCurrency(this.billingInfo?.balance || 0)}</div>
                    </div>
                </div>
            </div>
            <div class="col-md-4">
                <div class="card h-100 d-flex justify-content-center align-items-center">
                    <div class="card-body text-center">
                         <button id="recharge-btn" class="btn btn-success btn-lg"><i class="bi bi-plus-circle-fill me-2"></i> Recharge Balance</button>
                    </div>
                </div>
            </div>
        `;
        this.container.appendChild(summaryRow);
        summaryRow.querySelector('#recharge-btn').addEventListener('click', this.handleRechargeClick.bind(this));

        const historyContainer = document.createElement('div');
        historyContainer.className = 'billing-history-container pt-4';
        if (this.historyView) this.historyView.render().then(el => historyContainer.appendChild(el));
        this.container.appendChild(historyContainer);

        return this.container;
    }

    formatCurrency(amountInCents) {
        const currency = this.billingInfo?.planSnapshot?.currency || 'INR';
        if (amountInCents === null || amountInCents === undefined) return 'N/A';
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountInCents / 100);
    }

    destroy() {
        this.appClient.events.local.off('userBalanceUpdated', this._boundHandleBalanceUpdate);
        super.destroy();
    }
}